import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import agent from '@root/agent';
import ThinkingMessage from '@root/components/ViperAI/ThinkingMessage';
import { SeverityWithText } from '@root/components/bootstrapTable/badges/severity';
import DateFormatPipe from '@root/components/utilities/DateFormatPipe';
import { Button } from '@root/components/v3/button';
import { Input } from '@root/components/v3/input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@root/components/v3/table';
import { useMessageProgress } from '@root/hooks/useMessageProgress';
import { useQuery } from '@root/hooks/useQuery';
import { ChevronLeft, ChevronRight, SquarePen, ThumbsDown, ThumbsUp, Trash2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { toast } from 'sonner';
import './dashboard.scss';

const StarIcon = ({ width, height }) => (
  <svg xmlns="http://www.w3.org/2000/svg" width={width} height={height} viewBox="0 0 17 23" fill="none">
    <path
      d="M9.43085 8.84685C9.58667 8.3794 10.2468 8.3794 10.4026  8.84685L11.3162 11.5906C11.5173 12.1938 11.8562 12.7418 12.3059 13.1913C12.7556 13.6407 13.3038 13.9792 13.907 14.18L16.6494 15.0937C17.1169 15.2495 17.1169 15.9096 16.6494 16.0654L13.9056 16.9791C13.3025 17.1802 12.7544 17.519 12.305 17.9687C11.8555 18.4184 11.517 18.9666 11.3162 19.5699L10.4026 22.3122C10.369 22.4146 10.3039 22.5037 10.2167 22.5669C10.1294 22.6301 10.0244 22.6641 9.91672 22.6641C9.80899 22.6641 9.70401 22.6301 9.61676 22.5669C9.52951 22.5037 9.46445 22.4146 9.43085 22.3122L8.5172 19.5684C8.31624 18.9654 7.97762 18.4175 7.52818 17.9681C7.07874 17.5186 6.53082 17.18 5.92781 16.9791L3.18402 16.0654C3.08166 16.0318 2.99253 15.9667 2.92934 15.8795C2.86614 15.7922 2.83212 15.6873 2.83212 15.5795C2.83212 15.4718 2.86614 15.3668 2.92934 15.2796C2.99253 15.1923 3.08166 15.1273 3.18402 15.0937L5.92781 14.18C6.53082 13.9791 7.07874 13.6404 7.52818 13.191C7.97762 12.7416 8.31624 12.1936 8.5172 11.5906L9.43085 8.84685ZM3.95886 1.62404C3.97917 1.56269 4.0183 1.50931 4.07068 1.47148C4.12307 1.43364 4.18604 1.41328 4.25066 1.41328C4.31528 1.41328 4.37825 1.43364 4.43064 1.47148C4.48302 1.50931 4.52215 1.56269 4.54246 1.62404L5.09065 3.27003C5.33571 4.00378 5.91082 4.57889 6.64457 4.82394L8.29056 5.37214C8.3519 5.39245 8.40528 5.43158 8.44312 5.48396C8.48095 5.53634 8.50132 5.59932 8.50132 5.66394C8.50132 5.72856 8.48095 5.79153 8.44312 5.84391C8.40528 5.8963 8.3519 5.93543 8.29056 5.95574L6.64457 6.50393C6.28247 6.62411 5.95344 6.82716 5.68366 7.09694C5.41389 7.36671 5.21083 7.69575 5.09065 8.05785L4.54246 9.70384C4.52215 9.76518 4.48302 9.81856 4.43064 9.8564C4.37825 9.89423 4.31528 9.9146 4.25066 9.9146C4.18604 9.9146 4.12307 9.89423 4.07068 9.8564C4.0183 9.81856 3.97917 9.76518 3.95886 9.70384L3.41067 8.05785C3.29049 7.69575 3.08743 7.36671 2.81766 7.09694C2.54788 6.82716 2.21885 6.62411 1.85675 6.50393L0.210759 5.95574C0.149416 5.93543 0.0960346 5.8963 0.0581995 5.84391C0.0203643 5.79153 0 5.72856 0 5.66394C0 5.59932 0.0203643 5.53634 0.0581995 5.48396C0.0960346 5.43158 0.149416 5.39245 0.210759 5.37214L1.85675 4.82394C2.21885 4.70377 2.54788 4.50071 2.81766 4.23094C3.08743 3.96116 3.29049 3.63212 3.41067 3.27003L3.95886 1.62404ZM13.9722 0.138114C13.9862 0.0977805 14.0124 0.0628062 14.0472 0.0380536C14.0819 0.0133009 14.1236 0 14.1663 0C14.209 0 14.2506 0.0133009 14.2854 0.0380536C14.3201 0.0628062 14.3463 0.0977805 14.3603 0.138114L14.7258 1.2345C14.8887 1.72461 15.2726 2.10849 15.7627 2.27138L16.8591 2.63685C16.8994 2.65083 16.9344 2.67703 16.9591 2.71181C16.9839 2.74659 16.9972 2.78822 16.9972 2.83091C16.9972 2.8736 16.9839 2.91522 16.9591 2.95C16.9344 2.98478 16.8994 3.01099 16.8591 3.02497L15.7627 3.39043C15.5214 3.47117 15.3021 3.60686 15.1221 3.7868C14.9422 3.96673 14.8065 4.186 14.7258 4.42732L14.3603 5.5237C14.3463 5.56404 14.3201 5.59901 14.2854 5.62376C14.2506 5.64852 14.209 5.66182 14.1663 5.66182C14.1236 5.66182 14.0819 5.64852 14.0472 5.62376C14.0124 5.59901 13.9862 5.56404 13.9722 5.5237L13.6067 4.42732C13.526 4.186 13.3903 3.96673 13.2104 3.7868C13.0304 3.60686 12.8112 3.47117 12.5699 3.39043L11.4749 3.02497C11.4346 3.01099 11.3996 2.98478 11.3748 2.95C11.3501 2.91522 11.3368 2.8736 11.3368 2.83091C11.3368 2.78822 11.3501 2.74659 11.3748 2.71181C11.3996 2.67703 11.4346 2.65083 11.4749 2.63685L12.5713 2.27138C13.0614 2.10849 13.4453 1.72461 13.6082 1.2345L13.9722 0.138114Z"
      fill="#00DBDE"
    />
  </svg>
);

const FollowUpQuestions = ({ questions, onQuestionClick }) => {
  if (!questions || questions.length === 0) return null;

  return (
    <div className="follow-up-questions">
      <h4 className="follow-up-title">Suggested follow-up questions</h4>
      <div className="follow-up-grid">
        {questions.map((question, index) => (
          <button key={index} className="follow-up-button" onClick={() => onQuestionClick(question)}>
            {question}
          </button>
        ))}
      </div>
    </div>
  );
};

// Helper function to detect and render severity values in text
const renderTextWithSeverity = text => {
  if (typeof text !== 'string') return text;

  const severityRegex =
    /\b(CRITICAL|HIGH|MEDIUM|LOW|INFO|INFORMATIONAL|Critical|High|Medium|Low|Info|Informational)\b/g;
  const parts = [];
  let lastIndex = 0;
  let match;

  while ((match = severityRegex.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.substring(lastIndex, match.index));
    }

    // Add the severity component
    parts.push(
      <span className="relative bottom-[3px]">
        <SeverityWithText key={`severity-${match.index}`} propsValue={match[0]} alignItems="baseline" />
      </span>,
    );

    lastIndex = match.index + match[0].length;
  }

  // Add remaining text
  if (lastIndex < text.length) {
    parts.push(text.substring(lastIndex));
  }

  return parts.length > 0 ? parts : text;
};

// Helper function to process children and replace severity text
const processChildrenForSeverity = children => {
  if (!children) return children;

  if (typeof children === 'string') {
    return renderTextWithSeverity(children);
  }

  if (Array.isArray(children)) {
    return children.map((child, index) => {
      if (typeof child === 'string') {
        return <span key={index}>{renderTextWithSeverity(child)}</span>;
      }
      return child;
    });
  }

  return children;
};

// Custom markdown components for ChatGPT/Claude-like rendering
const markdownComponents = {
  // Paragraphs
  p: ({ children }) => (
    <p
      style={{
        marginBottom: '1.5em',
        lineHeight: '1.8',
        color: '#374151',
        fontSize: '15px',
      }}>
      {processChildrenForSeverity(children)}
    </p>
  ),

  // Headings
  h1: ({ children }) => (
    <>
      <h1
        style={{
          fontSize: '24px',
          fontWeight: '700',
          marginTop: '2em',
          marginBottom: '1em',
          color: '#1f2937',
          lineHeight: '1.3',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          paddingBottom: '0.5em',
          borderBottom: '2px solid #e5e7eb',
        }}>
        <span style={{ fontSize: '28px' }}>📌</span>
        {children}
      </h1>
    </>
  ),
  h2: ({ children }) => (
    <>
      <h2
        style={{
          fontSize: '20px',
          fontWeight: '700',
          marginTop: '2em',
          marginBottom: '1em',
          color: '#1f2937',
          lineHeight: '1.3',
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          paddingBottom: '0.4em',
          borderBottom: '1px solid #e5e7eb',
        }}>
        <span style={{ fontSize: '22px' }}>📍</span>
        {children}
      </h2>
    </>
  ),
  h3: ({ children }) => (
    <h3
      style={{
        fontSize: '18px',
        fontWeight: '600',
        marginTop: '1.75em',
        marginBottom: '0.85em',
        color: '#374151',
        lineHeight: '1.4',
      }}>
      {children}
    </h3>
  ),

  // Lists
  ul: ({ children }) => (
    <ul
      style={{
        marginTop: '1em',
        marginBottom: '1.75em',
        paddingLeft: '1.5em',
        listStyleType: 'disc',
      }}>
      {children}
    </ul>
  ),
  ol: ({ children }) => (
    <ol
      style={{
        marginTop: '1em',
        marginBottom: '1.75em',
        paddingLeft: '1.5em',
        listStyleType: 'decimal',
      }}>
      {children}
    </ol>
  ),
  li: ({ children }) => (
    <li
      style={{
        marginBottom: '0.65em',
        lineHeight: '1.8',
        color: '#374151',
      }}>
      {processChildrenForSeverity(children)}
    </li>
  ),

  // Code blocks
  code: ({ inline, children, ...props }) => {
    // Convert children to string to check its content
    const codeContent = String(children || '');
    const hasNewlines = codeContent.includes('\n');
    const isShort = codeContent.length < 80;

    // Treat as inline if: explicitly inline, OR it's short without newlines
    const shouldBeInline = inline || (!hasNewlines && isShort);

    if (shouldBeInline) {
      return (
        <code
          style={{
            backgroundColor: 'rgba(175, 184, 193, 0.2)',
            padding: '0.2em 0.4em',
            borderRadius: '3px',
            fontSize: '0.9em',
            fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
            color: '#1f2937',
          }}
          {...props}>
          {children}
        </code>
      );
    }

    // Block-level code for longer snippets or multi-line code
    return (
      <code
        style={{
          display: 'block',
          backgroundColor: '#f7f7f8',
          color: '#1f2937',
          padding: '1.25em',
          borderRadius: '8px',
          overflow: 'auto',
          fontSize: '14px',
          fontFamily: 'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace',
          lineHeight: '1.7',
          marginTop: '1em',
          marginBottom: '1.75em',
          border: '1px solid #e5e7eb',
        }}
        {...props}>
        {children}
      </code>
    );
  },
  pre: ({ children }) => <pre style={{ marginBottom: '1em', margin: 0 }}>{children}</pre>,

  // Emphasis
  strong: ({ children }) => (
    <strong style={{ fontWeight: '700', color: '#1f2937' }}>{processChildrenForSeverity(children)}</strong>
  ),
  em: ({ children }) => <em style={{ fontStyle: 'italic' }}>{processChildrenForSeverity(children)}</em>,

  // Links
  a: ({ href, children }) => (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      style={{
        color: '#9362d9',
        textDecoration: 'underline',
        textDecorationColor: 'rgba(147, 98, 217, 0.4)',
        textUnderlineOffset: '2px',
        transition: 'all 0.2s',
      }}
      onMouseEnter={e => {
        e.currentTarget.style.textDecorationColor = '#9362d9';
      }}
      onMouseLeave={e => {
        e.currentTarget.style.textDecorationColor = 'rgba(147, 98, 217, 0.4)';
      }}>
      {children}
    </a>
  ),

  // Blockquotes
  blockquote: ({ children }) => (
    <blockquote
      style={{
        borderLeft: '4px solid #9362d9',
        paddingLeft: '1.25em',
        paddingTop: '0.5em',
        paddingBottom: '0.5em',
        marginLeft: '0',
        marginTop: '1.5em',
        marginBottom: '1.75em',
        color: '#6b7280',
        fontStyle: 'italic',
        backgroundColor: 'rgba(147, 98, 217, 0.05)',
        borderRadius: '4px',
      }}>
      {processChildrenForSeverity(children)}
    </blockquote>
  ),

  // Horizontal rule
  hr: () => (
    <hr
      style={{
        border: 'none',
        borderTop: '2px solid rgba(229, 231, 235, 0.5)',
        marginTop: '2.5em',
        marginBottom: '2.5em',
      }}
    />
  ),

  // Tables
  table: ({ children }) => (
    <div
      style={{
        overflowX: 'auto',
        marginTop: '1.5em',
        marginBottom: '2em',
        border: '1px solid #e5e7eb',
        borderRadius: '8px',
      }}>
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          fontSize: '14px',
        }}>
        {children}
      </table>
    </div>
  ),
  th: ({ children }) => (
    <th
      style={{
        padding: '12px 16px',
        textAlign: 'left',
        borderBottom: '2px solid #e5e7eb',
        fontWeight: '600',
        color: '#374151',
        backgroundColor: '#f9fafb',
      }}>
      {children}
    </th>
  ),
  td: ({ children }) => (
    <td
      style={{
        padding: '12px 16px',
        borderBottom: '1px solid #e5e7eb',
        color: '#374151',
      }}>
      {processChildrenForSeverity(children)}
    </td>
  ),
};

const CopyIcon = ({ onClick, copied }) => (
  <span className="copy-icon" onClick={onClick} title={copied ? 'Copied!' : 'Copy'}>
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
    </svg>
    {copied && <span style={{ marginLeft: 4, color: '#16a34a', fontWeight: 600 }}>Copied!</span>}
  </span>
);

const ShareIcon = ({ onClick, copied }) => (
  <span className="copy-icon" onClick={onClick} title={copied ? 'Link Copied!' : 'Share'}>
    <svg
      width="18"
      height="18"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round">
      <circle cx="18" cy="5" r="3"></circle>
      <circle cx="6" cy="12" r="3"></circle>
      <circle cx="18" cy="19" r="3"></circle>
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49"></line>
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49"></line>
    </svg>
    {copied && <span style={{ marginLeft: 4, color: '#16a34a', fontWeight: 600 }}>Link!</span>}
  </span>
);

// Custom cell component for handling arrays and objects
const ArrayCell = ({ value, columnName }) => {
  const [showTooltip, setShowTooltip] = useState(false);

  // Helper function to check if a value is a severity level
  const isSeverityValue = val => {
    if (typeof val !== 'string') return false;
    const upperVal = val.toUpperCase();
    return ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'INFO', 'INFORMATIONAL'].includes(upperVal);
  };

  // Helper function to check if column is severity-related
  const isSeverityColumn = () => {
    if (!columnName) return false;
    return columnName.toLowerCase().includes('severity') || columnName.toLowerCase().includes('risk_level');
  };

  // Helper function to safely convert any value to string
  const safeToString = val => {
    if (val === null || val === undefined) return '-';
    if (typeof val === 'string') return val;
    if (typeof val === 'number' || typeof val === 'boolean') return String(val);
    if (Array.isArray(val)) return val.map(safeToString).join(', ');
    if (typeof val === 'object') return JSON.stringify(val);
    return String(val);
  };

  // Helper function to format object properties for display
  const formatObject = obj => {
    if (!obj || typeof obj !== 'object') return obj;

    // For rule objects, show key information in a readable format
    if (obj.rule_name || obj.resource_type || obj.rule_severity) {
      const parts = [];
      if (obj.rule_name) parts.push(`Rule: ${obj.rule_name}`);
      if (obj.rule_severity) parts.push(`Severity: ${obj.rule_severity}`);
      if (obj.rule_evaluation) parts.push(`Status: ${obj.rule_evaluation}`);
      if (obj.compliance_family && Array.isArray(obj.compliance_family)) {
        parts.push(`Compliance: ${obj.compliance_family.length} standards`);
      }
      return parts.join(' | ');
    }

    // For other objects, show key-value pairs
    const keyValuePairs = Object.entries(obj)
      .slice(0, 3)
      .map(([key, val]) => {
        if (Array.isArray(val)) {
          return `${key}: [${val.length} items]`;
        }
        if (typeof val === 'object' && val !== null) {
          return `${key}: {...}`;
        }
        return `${key}: ${val}`;
      });

    const result = keyValuePairs.join(', ');
    return Object.keys(obj).length > 3 ? `${result}...` : result;
  };

  if (Array.isArray(value)) {
    if (value.length === 0) return <span>-</span>;
    if (value.length === 1) {
      const firstItem = value[0];
      if (typeof firstItem === 'object' && firstItem !== null) {
        return <span>{formatObject(firstItem)}</span>;
      }
      return <span>{safeToString(firstItem)}</span>;
    }

    const firstValue =
      typeof value[0] === 'object' && value[0] !== null ? formatObject(value[0]) : safeToString(value[0]);
    const remainingCount = value.length - 1;

    return (
      <div style={{ position: 'relative', display: 'inline-block' }}>
        <span>{firstValue}</span>
        <span
          style={{
            color: '#6b7280',
            marginLeft: '4px',
            cursor: 'pointer',
            textDecoration: 'underline',
          }}
          onMouseEnter={() => setShowTooltip(true)}
          onMouseLeave={() => setShowTooltip(false)}>
          +{remainingCount}
        </span>
        {showTooltip && (
          <div
            style={{
              position: 'absolute',
              top: '-100%',
              left: '0',
              background: '#1f2937',
              color: 'white',
              padding: '8px 12px',
              borderRadius: '6px',
              fontSize: '12px',
              zIndex: 1000,
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              maxWidth: '300px',
              wordWrap: 'break-word',
              whiteSpace: 'normal',
            }}>
            {value
              .slice(1)
              .map(item => (typeof item === 'object' && item !== null ? formatObject(item) : safeToString(item)))
              .join(', ')}
          </div>
        )}
      </div>
    );
  }

  // Handle single object
  if (typeof value === 'object' && value !== null) {
    return <span>{formatObject(value)}</span>;
  }

  // Handle severity values - use SeverityWithText component
  if (isSeverityColumn() && isSeverityValue(value)) {
    return <SeverityWithText propsValue={value} />;
  }

  // Check if it's a direct severity value even without column name hint
  if (isSeverityValue(value)) {
    return <SeverityWithText propsValue={value} />;
  }

  return <span>{safeToString(value)}</span>;
};

// Custom table component for ViperAI using v3 table
const ViperTable = ({ data, columns }) => {
  if (!data || !columns || !Array.isArray(data) || !Array.isArray(columns)) {
    return <div>Invalid table data</div>;
  }

  return (
    <div
      className="viper-ai-table-wrapper"
      style={{
        border: '1px solid rgba(229, 231, 235, 0.5)',
        borderRadius: '12px',
        overflow: 'hidden',
        background: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(10px)',
        WebkitBackdropFilter: 'blur(10px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05), 0 1px 3px rgba(0, 0, 0, 0.03)',
      }}>
      <div
        style={{
          maxHeight: '400px',
          overflowY: 'auto',
          position: 'relative',
        }}>
        <Table>
          <TableHeader
            style={{
              position: 'sticky',
              top: 0,
              background: 'rgba(249, 250, 251, 0.95)',
              backdropFilter: 'blur(8px)',
              WebkitBackdropFilter: 'blur(8px)',
              zIndex: 1000,
            }}>
            <TableRow>
              {columns.map((column, index) => {
                // Define column widths - customize based on your needs

                return (
                  <TableHead
                    key={index}
                    style={{
                      padding: '12px 16px',
                      fontWeight: '600',
                      color: '#374151',
                      borderBottom: '2px solid rgba(229, 231, 235, 0.8)',
                      fontSize: '14px',
                      width: '150px',
                      minWidth: '150px', // Ensure minimum width for readability
                    }}>
                    {String(column)
                      .replace(/_/g, ' ')
                      .replace(/\b\w/g, l => l.toUpperCase())}
                  </TableHead>
                );
              })}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.map((row, rowIndex) => (
              <TableRow key={rowIndex}>
                {columns.map((column, colIndex) => (
                  <TableCell
                    key={colIndex}
                    style={{
                      padding: '12px 16px',
                      verticalAlign: 'top',
                      fontSize: '14px',
                    }}>
                    <ArrayCell value={column?.includes('at') ? row?.[column] : row?.[column]} columnName={column} />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};

// TableComponent to handle tabular_data structure
const TableComponent = ({ tableData }) => {
  if (!tableData || !tableData.columns || !tableData.data || !Array.isArray(tableData.data)) {
    return null;
  }

  // Filter columns to only show string, number, or boolean values
  // Exclude objects and arrays completely
  const filteredColumns = tableData.columns.filter(column => {
    // Check multiple rows to ensure the column consistently has renderable values
    const sampleValues = tableData.data.slice(0, 3).map(row => row[column]);
    return sampleValues.every(value => {
      if (value === null || value === undefined) return true; // Allow null/undefined
      if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return true;
      return false; // Exclude objects and arrays completely
    });
  });

  if (filteredColumns.length === 0) {
    return null;
  }

  return (
    <div
      className="viper-table-container"
      style={{ marginTop: '16px', marginBottom: '16px', width: '700px', overflowX: 'auto' }}>
      <ViperTable data={tableData.data} columns={filteredColumns} />
    </div>
  );
};

const getCopyText = msg => {
  // Handle new format where content is directly on the message object
  if (typeof msg.content === 'string') return msg.content;
  // Handle old format where content is nested under text
  if (typeof msg.text === 'string') return msg.text;
  if (msg.text?.content) return msg.text.content;
  if (msg.text?.message?.content) return msg.text.message.content;
  return '';
};

const MessageContent = ({ text, role, message }) => {
  const [feedback, setFeedback] = useState(null);
  const [showCommentInput, setShowCommentInput] = useState(false);
  const [comment, setComment] = useState('');
  const [copied, setCopied] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);
  const [isSubmittingFeedback, setIsSubmittingFeedback] = useState(false);

  const handleFeedback = type => {
    // If clicking the same button that's already active, cancel feedback
    if (feedback === type) {
      setFeedback(null);
      setShowCommentInput(false);
      setComment('');
      return;
    }

    // Set the new feedback type and show comment input
    setFeedback(type);
    setShowCommentInput(true);
  };

  const submitFeedback = async () => {
    // Get conversation and message IDs
    const { conversation_id, message_id } = getIds();

    if (!conversation_id || !message_id) {
      console.error('Missing conversation_id or message_id for feedback');
      toast.error('Unable to submit feedback: missing message information');
      return;
    }

    // Determine action based on feedback type
    const action = feedback === 'up' ? 'like' : 'dislike';

    setIsSubmittingFeedback(true);
    try {
      await agent.ViperAI.submit_feedback(conversation_id, message_id, action, comment || '');
      toast.success('Feedback submitted successfully');
      // Hide comment input but keep feedback state to show active state
      setShowCommentInput(false);
      setComment('');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Failed to submit feedback. Please try again.');
    } finally {
      setIsSubmittingFeedback(false);
    }
  };

  const cancelFeedback = () => {
    setFeedback(null);
    setShowCommentInput(false);
    setComment('');
  };

  const handleCopy = async () => {
    try {
      let textToCopy = getCopyText(text);
      if (!textToCopy) {
        textToCopy = getCopyText(message);
      }
      await navigator.clipboard.writeText(textToCopy || '');
      setCopied(true);
      setTimeout(() => setCopied(false), 1200);
    } catch (err) {
      console.error('Copy failed: ', err);
    }
  };

  // Helper to extract conversation_id and message_id
  const getIds = () => {
    // Try to get from message prop, fallback to text
    // Handle new format where properties are directly on the message object
    let conversation_id =
      message?.conversation_id ||
      message?.text?.message?.conversation_id ||
      message?.text?.conversation_id ||
      message?.text?.conversationId;
    let message_id = message?.id || message?.text?.message?.id || message?.text?.id;
    return { conversation_id, message_id };
  };

  const handleShare = () => {
    const { conversation_id, message_id } = getIds();
    if (conversation_id && message_id) {
      const url = `${window.location.origin}/viper-ai?conversation_id=${conversation_id}&message_id=${message_id}`;
      navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 1200);
    } else {
      // fallback: just copy current page
      navigator.clipboard.writeText(window.location.href);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 1200);
    }
  };

  // Helper function to extract message content for display
  const extractMessageContent = (text, message) => {
    // Handle new format where content is directly on the message object
    if (typeof message?.content === 'string') return message.content;
    if (typeof text === 'string') return text;

    // Handle old nested formats
    if (typeof message?.message?.content === 'string') return message.message.content;
    if (typeof message?.text === 'string') return message.text;
    if (typeof message?.text?.content === 'string') return message.text.content;

    return 'Could not parse message content';
  };

  try {
    // First try to parse if text is a JSON string
    let messageData;
    if (typeof text === 'string') {
      try {
        messageData = JSON.parse(text);
      } catch (e) {
        // If text is not valid JSON, use it directly as content
        messageData = { content: text };
      }
    } else if (text && typeof text === 'object') {
      // If text is already an object (like from your sample)
      messageData = text;
    }

    // Extract message content and tabular data
    const messageContent = extractMessageContent(text, messageData);
    // const tabularData = extractTabularData(text); // Pass the original message object, not messageData

    // Helper function to format timestamp
    const formatTimestamp = timestamp => {
      if (!timestamp) return null;

      // Check if it's already in "YYYY-MM-DD, HH:MM AM/PM" format (e.g. "2025-11-05, 02:00 PM")
      // If so, return it directly or reformat as necessary.

      if (typeof timestamp === 'string' && /^\d{4}-\d{2}-\d{2}, \d{2}:\d{2} [AP]M$/.test(timestamp.trim())) {
        // Could further reformat to desired style (e.g. "Nov 5, 2:00 PM")
        // Parse manually:
        try {
          const [datePart, timePart] = timestamp.split(',');
          const [year, month, day] = datePart.split('-').map(Number);
          let [hourStr, minuteAMPM] = timePart.trim().split(':');
          const minute = minuteAMPM.slice(0, 2);
          const ampm = minuteAMPM.slice(3, 5);
          let hour = Number(hourStr.trim());
          // 12-hour format adjustments
          if (ampm === 'PM' && hour !== 12) hour += 12;
          if (ampm === 'AM' && hour === 12) hour = 0;
          const dateObj = new Date(year, month - 1, day, hour, Number(minute));
          return dateObj.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
          });
        } catch (err) {
          return timestamp;
        }
      }

      // Otherwise, fall back to native Date parsing
      try {
        const date = new Date(timestamp);
        if (isNaN(date.getTime())) return timestamp;
        return date.toLocaleString('en-US', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          hour12: true,
        });
      } catch (e) {
        return timestamp;
      }
    };

    return (
      <div>
        {/* Always show overview content first */}
        <div className="markdown-content markdown_viper">
          <ReactMarkdown components={markdownComponents} remarkPlugins={[remarkGfm]}>
            {messageContent}
          </ReactMarkdown>
        </div>

        {/* Show timestamp if available */}
        {message?.created_at && (
          <div
            style={{
              fontSize: '12px',
              color: '#6b7280',
              marginTop: '8px',
              // marginBottom: '8px',
              fontStyle: 'italic',
            }}>
            {formatTimestamp(DateFormatPipe(message.created_at, true, false))}
          </div>
        )}

        {/* If table data exists, show it below the overview */}
        {message?.tabular_data && Array.isArray(message.tabular_data) && message.tabular_data.length > 0 && (
          <div style={{ marginTop: 24 }}>
            {message.tabular_data.map((tableData, index) => (
              <div key={index}>
                <TableComponent tableData={tableData} />
              </div>
            ))}
          </div>
        )}
        {role === 'assistant' && (
          <div className="message-actions">
            <div className="actions-group">
              <button className="action-button" onClick={handleCopy}>
                <CopyIcon copied={copied} />
              </button>
              <button
                className={`action-button ${feedback === 'up' ? 'active' : ''}`}
                onClick={() => handleFeedback('up')}>
                <ThumbsUp size={14} />
              </button>
              <button
                className={`action-button ${feedback === 'down' ? 'active' : ''}`}
                onClick={() => handleFeedback('down')}>
                <ThumbsDown size={14} />
              </button>
              <button className="action-button" onClick={handleShare}>
                <ShareIcon copied={shareCopied} />
              </button>
            </div>
            {showCommentInput && (
              <div
                style={{
                  marginTop: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '6px 8px',
                  backgroundColor: '#f9fafb',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                }}>
                <Input
                  type="text"
                  value={comment}
                  onChange={e => setComment(e.target.value)}
                  placeholder="Add feedback (optional)"
                  className="flex-1 px-2 py-1 rounded border-none bg-transparent text-[13px] outline-none text-gray-700"
                  maxLength={500}
                  autoFocus
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      submitFeedback();
                    }
                    if (e.key === 'Escape') {
                      cancelFeedback();
                    }
                  }}
                />
                <Button onClick={submitFeedback} disabled={isSubmittingFeedback} className="action-button">
                  {isSubmittingFeedback ? '...' : 'Submit'}
                  Submit Feedback{' '}
                </Button>
                <Button
                  variant="outline"
                  onClick={cancelFeedback}
                  disabled={isSubmittingFeedback}
                  className="action-button">
                  ✕ Cancel
                </Button>
              </div>
            )}
          </div>
        )}
      </div>
    );
  } catch (e) {
    console.error('Error processing response:', e);
    // Fallback display if something goes wrong
    return <p style={{ marginBottom: '0px' }}>{typeof text === 'string' ? text : 'Error displaying message'}</p>;
  }
};

const ViperAIChat = () => {
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [conversations, setConversations] = useState([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [inputError, setInputError] = useState('');
  const [pollingMessageId, setPollingMessageId] = useState(null);
  const [pollingConversationId, setPollingConversationId] = useState(null);
  const [currentFollowUps, setCurrentFollowUps] = useState([]);
  const CONVERSATIONS_PER_PAGE = 25;
  const sidebarRef = useRef(null);
  const textareaRef = useRef(null);
  const messagesEndRef = useRef(null);
  let searchTimeout = useRef();
  const query = useQuery();
  // const { copyContext } = useCopyTrackingService();

  const { progress, followUpSuggestions } = useMessageProgress(pollingConversationId, pollingMessageId, isThinking);

  // Stop thinking when progress is complete and fetch final response
  useEffect(() => {
    if (progress?.status === 'complete' && pollingConversationId) {
      setIsThinking(false);

      // Set follow-ups from progress
      if (followUpSuggestions && followUpSuggestions.length > 0) {
        setCurrentFollowUps(followUpSuggestions);
      }

      // Fetch the latest conversation to get the assistant response
      const fetchFinalResponse = async () => {
        try {
          const response = await agent.ViperAI.get_viper_ai_chat_history(pollingConversationId);
          if (response && response.messages && Array.isArray(response.messages)) {
            // Find the latest assistant message
            const assistantMessages = response.messages.filter(
              msg => msg.role === 'assistant' || msg.message?.role === 'assistant',
            );

            if (assistantMessages.length > 0) {
              const latestAssistant = assistantMessages[assistantMessages.length - 1];

              // Add the assistant message if not already present
              setMessages(prevMessages => {
                const lastMessage = prevMessages[prevMessages.length - 1];
                if (lastMessage?.role !== 'assistant') {
                  return [...prevMessages, latestAssistant];
                }
                return prevMessages;
              });
            }
          }
        } catch (error) {
          console.error('Error fetching final response:', error);
          // Add fallback message
          setMessages(prevMessages => {
            const lastMessage = prevMessages[prevMessages.length - 1];
            if (lastMessage?.role !== 'assistant') {
              return [
                ...prevMessages,
                {
                  role: 'assistant',
                  content: 'Analysis complete! Your security analysis has been processed successfully.',
                },
              ];
            }
            return prevMessages;
          });
        } finally {
          setPollingMessageId(null);
          setPollingConversationId(null);
        }
      };

      fetchFinalResponse();
    }
  }, [progress, followUpSuggestions, pollingConversationId]);

  // Function to fetch conversations
  const fetchConversations = useCallback(async () => {
    if (!hasMore || isLoadingMore) return;

    try {
      setIsLoadingMore(true);
      const skip = (page - 1) * CONVERSATIONS_PER_PAGE;
      const response = await agent.ViperAI.get_list_conversation({
        limit: CONVERSATIONS_PER_PAGE,
        skip,
      });

      // Append new conversations to existing ones
      setConversations(prev => {
        // Filter out any duplicates using conversation IDs
        const newConversations = response.conversations || [];
        const existingIds = new Set(prev.map(c => c.id));
        const uniqueNewConversations = newConversations.filter(c => !existingIds.has(c.id));
        return [...prev, ...uniqueNewConversations];
      });

      // Update hasMore based on whether we received a full page of results
      setHasMore((response.conversations || []).length === CONVERSATIONS_PER_PAGE);
      setPage(prev => prev + 1);
    } catch (error) {
      console.error('Error fetching conversations:', error);
    } finally {
      setIsLoadingMore(false);
    }
  }, [hasMore, isLoadingMore, page]);

  // Fetch initial conversations
  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Handle sidebar scroll
  const handleSidebarScroll = useCallback(() => {
    if (!sidebarRef.current || isLoadingMore || !hasMore || searchValue) return;

    const { scrollTop, scrollHeight, clientHeight } = sidebarRef.current;
    // Load more when user scrolls to 80% of the list
    if (scrollHeight - scrollTop <= clientHeight * 1.2) {
      fetchConversations();
    }
  }, [isLoadingMore, hasMore, searchValue, fetchConversations]);

  // Add scroll event listener
  useEffect(() => {
    const sidebar = sidebarRef.current;
    if (sidebar) {
      sidebar.addEventListener('scroll', handleSidebarScroll);
      return () => sidebar.removeEventListener('scroll', handleSidebarScroll);
    }
  }, [handleSidebarScroll]);

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    if (messages.length > 0 || isThinking) {
      scrollToBottom();
    }
  }, [messages, isThinking, scrollToBottom]);

  // Filter conversations based on search
  const conversationsToShow = useMemo(() => {
    if (!searchValue) return conversations;
    return conversations.filter(item => item.title?.toLowerCase().includes(searchValue.toLowerCase()));
  }, [conversations, searchValue]);

  // Conversation search effect
  useEffect(() => {
    const trimmed = searchValue.trim();
    if (trimmed === '') {
      setConversations([]);
      return;
    }
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(async () => {
      try {
        const result = await agent.ViperAI.get_conversation_search(trimmed);
        setConversations(result?.conversations || []);
      } catch (e) {
        setConversations([]);
      }
    }, 350);
    return () => clearTimeout(searchTimeout.current);
  }, [searchValue]);

  const handleSend = useCallback(
    async (text = inputText) => {
      // Get conversation ID based on messages or fetch a new one
      let conversationId =
        messages?.find(item => {
          const role = item.message?.role || item.role;
          return role === 'assistant';
        })?.message?.conversation_id ||
        messages?.find(item => item.message?.conversation_id || item.conversation_id)?.conversation_id ||
        messages?.[0]?.message?.conversation_id ||
        messages?.[0]?.conversation_id;

      // If no conversationId exists, fetch a new one
      if (messages.length === 0 && !conversationId) {
        try {
          conversationId = await agent.ViperAI.get_conversation_id({});
        } catch (error) {
          console.error('Error fetching conversation ID:', error);
          return; // Early exit if conversation ID can't be fetched
        }
      }

      // Proceed only if there is text and a valid conversationId
      if (text.trim() && conversationId) {
        // Update messages state optimistically - use new format
        setMessages(prevMessages => [...prevMessages, { role: 'user', content: text }]);
        setInputText('');
        setIsThinking(true);

        // Start progress polling immediately
        const targetConversationId = conversationId?.conversation?.id || conversationId;
        setPollingConversationId(targetConversationId);

        // Generate a message ID for polling (UUID v4)
        const messageId = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
          var r = (Math.random() * 16) | 0,
            v = c == 'x' ? r : (r & 0x3) | 0x8;
          return v.toString(16);
        });

        // Start polling immediately with our generated ID
        setPollingMessageId(messageId);

        // Fire and forget the message request - don't wait for completion
        agent.ViperAI.get_viper_ai_chat_data({ content: text, message_id: messageId }, targetConversationId)
          .then(response => {
            if (response?.detail) {
              toast.warning('Message limit reached');
              setIsThinking(false);
              setPollingMessageId(null);
              setPollingConversationId(null);
              return;
            }

            // Don't add assistant message here - let progress completion handle it
          })
          .catch(error => {
            console.error('Error sending message:', error);
            // Only show error if progress hasn't completed yet
            if (isThinking) {
              setMessages(prevMessages => [
                ...prevMessages,
                {
                  role: 'assistant',
                  content: 'Sorry, I encountered an error processing your request. Please try again.',
                },
              ]);
              setIsThinking(false);
              setPollingMessageId(null);
              setPollingConversationId(null);
            }
          });

        // If no previous messages were there, trigger refetch for conversation list
        if (messages.length === 0) {
          fetchConversations();
        }
      }
    },
    [inputText, messages, fetchConversations, isThinking],
  );

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = '25px'; // Always reset to min-height
      if (inputText.length > 0) {
        textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
      }
    }
  }, [inputText]);

  const handleInputChange = e => {
    const value = e.target.value;
    if (value.length > 5000) {
      setInputError('Character limit exceeded. Maximum is 5000 characters.');
      return;
    } else {
      setInputError('');
    }
    setInputText(value);
    if (textareaRef.current) {
      textareaRef.current.style.height = '25px'; // Always reset to min-height
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
    }
  };

  const handleKeyPress = e => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleFollowUpClick = question => {
    handleSend(question);
  };

  const handleNewNotebookClick = () => {
    setMessages([]);
    fetchConversations();
  };

  const handleNotebookClick = async item => {
    try {
      const response = await agent.ViperAI.get_viper_ai_chat_history(item.id);
      if (!response) {
        throw new Error('Network response was not ok');
      }

      // Handle new format with messages array
      if (response.messages && Array.isArray(response.messages)) {
        setMessages(response.messages);
      } else if (Array.isArray(response)) {
        // Fallback for old format
        setMessages(response);
      } else if (response && response.message) {
        setMessages([response]);
      } else {
        setMessages(() => [
          {
            role: 'assistant',
            content: 'Sorry, I encountered an error processing your request. Please try again.',
          },
        ]);
      }
      // Update the URL with the conversation_id (remove message_id if present)
      const url = new URL(window.location.href);
      url.searchParams.set('conversation_id', item.id);
      url.searchParams.delete('message_id');
      window.history.pushState({}, '', url.pathname + url.search);
    } catch (error) {
      console.error('Error:', error);
      setMessages(() => [
        {
          role: 'assistant',
          content: 'Sorry, I encountered an error processing your request. Please try again.',
        },
      ]);
    } finally {
      setIsThinking(false);
    }
  };
  const handleDeleteNotebook = async id => {
    if (!id) {
      console.error('No conversation id provided for delete');
      return;
    }
    try {
      await agent.ViperAI.delete_conversation(id);
      // Reset pagination and conversations state
      setConversations([]);
      setPage(1);
      setHasMore(true);
      // Now fetch the first page of conversations
      fetchConversations();
      // If the deleted conversation is open, clear messages
      if (messages?.[0]?.message?.conversation_id === id || messages?.[0]?.conversation_id === id) {
        setMessages([]);
      }
    } catch (error) {
      console.error('Error deleting notebook:', error);
    }
  };

  // // Handle copy context to pre-fill input
  // useEffect(() => {
  //   if (copyContext && copyContext.copiedText) {
  //     const contextText = `Context: ${copyContext.breadcrumb} > ${copyContext.cardOrTableName}\n\nCopied text: ${copyContext.copiedText}`;
  //     setInputText(contextText);
  //   }
  // }, [copyContext]);

  useEffect(() => {
    const messageId = query.get('message_id');
    const conversationId = query.get('conversation_id');
    const queryText = query.get('query');

    if (conversationId) {
      // Fetch conversation by conversationId
      (async () => {
        try {
          const response = await agent.ViperAI.get_viper_ai_chat_history(conversationId);
          if (response) {
            // Handle new format with messages array
            if (response.messages && Array.isArray(response.messages)) {
              setMessages(response.messages);
            } else if (Array.isArray(response)) {
              // Fallback for old format
              setMessages(response);
            } else if (response && response.message) {
              setMessages([response]);
            }
            // Optionally, scroll to the message with messageId
            setTimeout(() => {
              const msgDiv = document.getElementById(`msg-${messageId}`);
              if (msgDiv) msgDiv.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 500);
          }
        } catch (e) {
          // fallback: do nothing
        }
      })();
    } else if (queryText) {
      // If there's a query parameter, automatically send it as a message
      setInputText(queryText);
      // Auto-send the query after a short delay to ensure component is ready
      setTimeout(() => {
        handleSend(queryText);
      }, 100);
    }
  }, [query, handleSend]);

  return (
    <div className="viper-ai-dashboard">
      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && deleteTarget && (
        <div className="delete-dialog-backdrop">
          <div className="delete-dialog">
            <h3>Delete Thread</h3>
            <p>
              Are you sure you want to delete "<b>{deleteTarget.title}</b>"? This action cannot be undone.
            </p>
            <div className="delete-dialog-actions">
              <button onClick={() => setShowDeleteDialog(false)} className="cancel-btn">
                Cancel
              </button>
              <button
                className="delete-btn"
                onClick={async () => {
                  if (deleteTarget && deleteTarget.id) {
                    await handleDeleteNotebook(deleteTarget.id);
                  }
                  setShowDeleteDialog(false);
                  setDeleteTarget(null);
                }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
      <div className={`sidebar${sidebarCollapsed ? ' collapsed' : ''}`} data-testid="sidebar">
        {sidebarCollapsed ? (
          <>
            <button
              className="sidebar-toggle-btn"
              onClick={() => setSidebarCollapsed(prev => !prev)}
              aria-label="Expand sidebar"
              style={{ margin: '0 auto 16px auto' }}>
              <ChevronRight size={24} />
            </button>
            <button
              className="new-notebook-btn"
              onClick={handleNewNotebookClick}
              style={{
                margin: '0 auto',
                width: 40,
                padding: 0,
                height: 40,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
              title="New Chat">
              <SquarePen size={15} />
            </button>
          </>
        ) : (
          <>
            <div className="sidebar-fixed-content">
              <div className="viper-ai-sidebar-header-row">
                <div className="viper-ai-sidebar-header">
                  <StarIcon width="25" height="25" />
                  <span>Viper AI</span>
                </div>
                <button
                  className="sidebar-toggle-btn"
                  onClick={() => setSidebarCollapsed(prev => !prev)}
                  aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                  style={{ marginLeft: 'auto' }}>
                  {sidebarCollapsed ? <ChevronRight size={24} /> : <ChevronLeft size={24} />}
                </button>
              </div>

              <button
                className="new-notebook-btn"
                onClick={handleNewNotebookClick}
                style={{
                  margin: sidebarCollapsed ? '0 auto' : '0px',
                  width: sidebarCollapsed ? 40 : 'auto',
                  padding: sidebarCollapsed ? 0 : undefined,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                }}
                title="New Chat">
                <SquarePen size={15} />
                {!sidebarCollapsed && <span style={{ marginLeft: 8 }}>New Notebook</span>}
              </button>

              <Input
                type="text"
                placeholder="Filter notebooks"
                className="search-input mb-2"
                value={searchValue}
                onChange={e => setSearchValue(e.target.value)}
              />
            </div>

            <div className="sidebar-scrollable-content" ref={sidebarRef}>
              <div className="notebook-section">
                <span>Recent Notebooks</span>
              </div>
              {conversationsToShow
                ?.filter(item => item.title !== null)
                .map((item, index) => (
                  <div
                    key={index}
                    className={`notebook-item${
                      item.id === (messages?.[0]?.message?.conversation_id || messages?.[0]?.conversation_id)
                        ? ' active'
                        : ''
                    }`}
                    onClick={() => handleNotebookClick(item)}
                    style={{ justifyContent: 'space-between' }}>
                    <div className="notebook-content">
                      <span className="notebook-title" title={item.title}>
                        {item.title}
                      </span>
                    </div>
                    <div
                      className="delete-icon"
                      onClick={e => {
                        e.stopPropagation();
                        setDeleteTarget(item);
                        setShowDeleteDialog(true);
                      }}>
                      <Trash2 size={16} />
                    </div>
                  </div>
                ))}
              {isLoadingMore && <div className="loading-more">Loading more conversations...</div>}
            </div>
          </>
        )}
      </div>

      <div className="main-content ">
        {messages?.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-content">
              <div className="purple-logo">
                <StarIcon width="35" height="35" />
              </div>

              <h1 className="title">Viper AI is your AI security analyst.</h1>
            </div>

            <p className="info-text">
              Viper AI supports questions associated with OS events, indicators, threat intelligence feeds, Shield logs,
              and the fields within them. The default time range for event search is the last 24 hours.
            </p>

            <div className="quick-tips-section">
              <h3 className="quick-tips-title">Quick Tips:</h3>
              <ul className="quick-tips-list">
                <li>Ask about specific security alerts or general threat analysis questions</li>
                <li>Query your cloud infrastructure compliance and security posture</li>
                <li>Analyze vulnerability data and remediation recommendations</li>
                <li>Investigate security incidents and attack patterns</li>
                <li>Get insights on risk assessment and security scoring</li>
                <li>Use the search function to find previous conversations</li>
              </ul>
            </div>

            <div className="input-container">
              <div className="chat-input">
                <div className="purple-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" width="22" height="20" viewBox="0 0 256 256">
                    <path
                      fill="currentColor"
                      d="m197.58 129.06l-51.61-19l-19-51.65a15.92 15.92 0 0 0-29.88 0L78.07 110l-51.65 19a15.92 15.92 0 0 0 0 29.88L78 178l19 51.62a15.92 15.92 0 0 0 29.88 0l19-51.61l51.65-19a15.92 15.92 0 0 0 0-29.88ZM140.39 163a15.87 15.87 0 0 0-9.43 9.43l-19 51.46L93 172.39a15.87 15.87 0 0 0-9.39-9.39l-51.46-19l51.46-19a15.87 15.87 0 0 0 9.39-9.39l19-51.46l19 51.46a15.87 15.87 0 0 0 9.43 9.43l51.46 19ZM144 40a8 8 0 0 1 8-8h16V16a8 8 0 0 1 16 0v16h16a8 8 0 0 1 0 16h-16v16a8 8 0 0 1-16 0V48h-16a8 8 0 0 1-8-8Zm104 48a8 8 0 0 1-8 8h-8v8a8 8 0 0 1-16 0v-8h-8a8 0 0 1 0-16h8v-8a8 0 0 1 16 0v8h8a8 8 0 0 1 8 8Z"
                    />
                  </svg>
                </div>
                <textarea
                  ref={textareaRef}
                  value={inputText}
                  onChange={handleInputChange}
                  onKeyPress={handleKeyPress}
                  placeholder="Ask Viper AI..."
                  className="text-input"
                  style={{
                    overflow: 'hidden',
                    resize: 'none',
                    minHeight: '25px',
                    height: '25px',
                  }}
                  maxLength={5000}
                />
                {inputError && (
                  <div
                    className="input-error"
                    style={{ color: 'red', fontSize: '13px', marginTop: '2px', marginLeft: '4px' }}>
                    {inputError}
                  </div>
                )}
                <button
                  onClick={() => handleSend()}
                  className={`send-button${isThinking ? ' disabled' : ''}${
                    inputText.trim().length > 0 ? ' active' : ''
                  }`}
                  disabled={isThinking}
                  title={isThinking ? 'Waiting for response...' : 'Send message'}>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeLinejoin="round">
                    <path d="M12 19V5M5 12l7-7 7 7" />
                  </svg>
                </button>
              </div>
              <p className="warning-text">Viper AI can make mistakes. Consider checking important information.</p>
            </div>
          </div>
        ) : (
          <>
            <div className="content-area">
              <div className="message-container">
                {messages.map((msg, index) => {
                  // Handle new format where message properties are directly on the object
                  const messageObj = msg.message || msg; // fallback for old format
                  const functionCalls = msg.function_calls || [];
                  const messageId = messageObj?.id || msg?.id;
                  const messageRole = messageObj?.role || msg?.role;
                  // const messageContent = messageObj?.content || messageObj?.text || msg?.content;
                  return (
                    <div
                      key={index}
                      id={messageId ? `msg-${messageId}` : undefined}
                      className={`message ${messageRole === 'user' ? 'user-message' : 'ai-message'}`}>
                      <MessageContent
                        text={
                          functionCalls.length > 0
                            ? { ...messageObj, function_calls: functionCalls, tabular_data: msg.tabular_data }
                            : { ...messageObj, tabular_data: msg.tabular_data }
                        }
                        role={messageRole}
                        message={{ ...messageObj, function_calls: functionCalls, tabular_data: msg.tabular_data }}
                      />
                    </div>
                  );
                })}

                {isThinking && <ThinkingMessage progress={progress} />}
                <div ref={messagesEndRef} />
              </div>
              {messages?.length > 0 &&
                (messages[messages.length - 1].role === 'assistant' ||
                  messages[messages.length - 1].message?.role === 'assistant') && (
                  <FollowUpQuestions
                    questions={(() => {
                      // First try to use follow-ups from progress tracking
                      if (currentFollowUps && currentFollowUps.length > 0) {
                        return currentFollowUps;
                      }

                      // Fallback to parsing from message content
                      try {
                        const lastMessage = messages[messages.length - 1];
                        const messageText =
                          lastMessage.text ||
                          lastMessage.content ||
                          lastMessage.message?.text ||
                          lastMessage.message?.content;
                        if (messageText && typeof messageText === 'string') {
                          const parsedResponse = JSON.parse(messageText);
                          return parsedResponse.next_suggestable_questions;
                        }
                        return [];
                      } catch (e) {
                        return [];
                      }
                    })()}
                    onQuestionClick={handleFollowUpClick}
                  />
                )}
              <div className="input-container" style={{ position: 'sticky', bottom: 0 }}>
                <div className="chat-input">
                  <div className="purple-icon">
                    <svg xmlns="http://www.w3.org/2000/svg" width="22" height="20" viewBox="0 0 256 256">
                      <path
                        fill="currentColor"
                        d="m197.58 129.06l-51.61-19l-19-51.65a15.92 15.92 0 0 0-29.88 0L78.07 110l-51.65 19a15.92 15.92 0 0 0 0 29.88L78 178l19 51.62a15.92 15.92 0 0 0 29.88 0l19-51.61l51.65-19a15.92 15.92 0 0 0 0-29.88ZM140.39 163a15.87 15.87 0 0 0-9.43 9.43l-19 51.46L93 172.39a15.87 15.87 0 0 0-9.39-9.39l-51.46-19l51.46-19a15.87 15.87 0 0 0 9.39-9.39l19-51.46l19 51.46a15.87 15.87 0 0 0 9.43 9.43l51.46 19ZM144 40a8 8 0 0 1 8-8h16V16a8 8 0 0 1 16 0v16h16a8 8 0 0 1 0 16h-16v16a8 8 0 0 1-16 0V48h-16a8 8 0 0 1-8-8Zm104 48a8 8 0 0 1-8 8h-8v8a8 8 0 0 1-16 0v-8h-8a8 0 0 1 0-16h8v-8a8 0 0 1 16 0v8h8a8 8 0 0 1 8 8Z"
                      />
                    </svg>
                  </div>
                  <textarea
                    ref={textareaRef}
                    value={inputText}
                    onChange={handleInputChange}
                    onKeyPress={handleKeyPress}
                    placeholder="Ask Viper AI..."
                    className="text-input"
                    style={{
                      overflow: 'hidden',
                      resize: 'none',
                      minHeight: '25px',
                      height: '25px',
                    }}
                    maxLength={5000}
                  />
                  {inputError && (
                    <div
                      className="input-error"
                      style={{ color: 'red', fontSize: '13px', marginTop: '2px', marginLeft: '4px' }}>
                      {inputError}
                    </div>
                  )}
                  <button
                    onClick={() => handleSend()}
                    className={`send-button${isThinking ? ' disabled' : ''}${
                      inputText.trim().length > 0 ? ' active' : ''
                    }`}
                    disabled={isThinking}
                    title={isThinking ? 'Waiting for response...' : 'Send message'}>
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round">
                      <path d="M12 19V5M5 12l7-7 7 7" />
                    </svg>
                  </button>
                </div>
                <p className="warning-text">Viper AI can make mistakes. Consider checking important information.</p>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default ViperAIChat;
